<template>
  <div class="table-wrapper healthchecks">
    <div>
      <b-button
        v-if="$can('create', 'healthchecks', 'cdn')"
        variant="bg-primary"
        @click="onCreateHealthcheck()"
      >
        <GjIcon
          class="icon-left"
          name="Plus"
        />
        Create Healthcheck
      </b-button>
      <b-button
        v-if="$can('update', 'healthchecks', 'cdn')"
        variant="bg-white"
        :disabled="!isSelected"
        class="d-none d-lg-inline"
        @click="onEditClick(selected[0].id)"
      >
        Edit
      </b-button>
      <button-loading
        v-if="$can('delete', 'healthchecks', 'cdn')"
        variant="bg-white"
        :disabled="!isSelected"
        :spinning="removingHealthcheck"
        class="d-none d-lg-inline"
        @click="onDeleteClick(selected[0].id)"
      >
        Delete
      </button-loading>
    </div>
    <!-- DESKTOP -->
    <div class="default-table position-relative d-none d-lg-block">
      <b-table
        ref="table"
        :items="healthchecks"
        :fields="fields"
        select-mode="single"
        show-empty
        selectable
        responsive
        :class="{ loaded: !loading }"
        :busy="loading"
        @row-selected="onRowSelected"
      >
        <template #cell(selected)="{ rowSelected }">
          <select-button :selected="rowSelected" />
        </template>

        <template #cell(name)="data">
          <a
            class="link"
            @click="onEditClick(data.item.id)"
          >{{
            data.value
          }}</a>
        </template>

        <template #cell(request)="data">
          <span class="tag">{{ data.item.requestMethod }}</span>
          {{ data.item.requestPath }}
        </template>

        <template #empty>
          <div
            v-if="loading"
            class="loading-table text-center py-1"
          >
            <b-spinner variant="primary" />
          </div>
          <template v-else>
            <no-data
              no-data-text="No distribution healthchecks have been added yet..."
              create-text="+ Create Healthcheck"
              access-page="Healthchecks"
              :has-permissions="hasHealthcheckPermissions"
              @action="onCreateHealthcheck()"
            />
          </template>
        </template>
      </b-table>
      <table-busy v-if="loading && healthchecks.length > 0" />
    </div>
    <!-- MOBILE -->
    <div class="position-relative d-lg-none mt-2">
      <div :class="{ 'opacity-7': loading || removingHealthcheck }">
        <template v-for="healthcheck in healthchecks">
          <card
            :key="healthcheck.id"
            :fields="fields"
            :item="healthcheck"
            class="top-fade-in"
          >
            <template #options>
              <b-dropdown
                id="dropdown-1"
                right
                variant="white"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'healthchecks', 'cdn')"
                  @click="onEditClick(healthcheck.id)"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'healthchecks', 'cdn')"
                  @click="onDeleteClick(healthcheck.id)"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>
            <template #name>
              <a
                class="link"
                @click="onEditClick(healthcheck.id)"
              >{{
                healthcheck.name
              }}</a>
            </template>
            <template #request>
              <span class="tag">{{ healthcheck.requestMethod }}</span>
              {{ healthcheck.requestPath }}
            </template>
          </card>
        </template>
      </div>
      <div
        v-if="loading || removingHealthcheck"
        class="mobile-loading"
      >
        <table-busy />
      </div>
      <template v-if="healthchecks.length < 1">
        <div
          v-if="loading"
          class="empty-loading-table"
        />
        <paper-simple
          v-else
          class="top-fade-in"
        >
          <no-data
            class="text-center"
            no-data-text="No distribution healthchecks have been added yet..."
            create-text="+ Create Healthcheck"
            access-page="Healthchecks"
            :has-permissions="hasHealthcheckPermissions"
            @action="onCreateHealthcheck()"
          />
        </paper-simple>
      </template>
    </div>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'

export default {
  name: 'DistributionHealthchecks',
  components: {
    Card,
  },
  data() {
    return {
      id: null,
      selected: [],
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'name', isPrimary: true },
        { key: 'request', label: 'Request Method & Path' },
        { key: 'host', isPrimary: true },
        'interval',
        'expectedResponse',
      ],
      hasHealthcheckPermissions: true,
    }
  },
  computed: {
    healthchecks() {
      return this.$store.state.cdn.healthchecks
    },
    removingHealthcheck() {
      return this.$store.state.cdn.removingHealthcheck
    },
    isSelected() {
      return this.selected[0]
    },
    loading() {
      return this.$store.state.cdn.fetchingHealthchecks
    },
  },
  created() {
    this.id = this.$route.params.id
    this.getHealthchecks()
  },
  methods: {
    onRowSelected(item) {
      this.selected = item
    },
    getHealthchecks() {
      this.$store.dispatch('cdn/fetchHealthchecks', this.id).catch(error => {
        if (error.response?.status === 403) {
          this.hasHealthcheckPermissions = false
          return
        }
        this.errorToast(
          error.response?.data?.errors[0]
            || 'Something went wrong while fetching healthchecks.',
        )
      })
    },
    onEditClick(healthcheckId) {
      this.$router.push({
        name: 'distribution-healthchecks-edit',
        params: { id: this.id, healthcheckId },
      })
    },
    onCreateHealthcheck() {
      this.$router.push({
        name: 'distribution-healthchecks-create',
        params: { id: this.id },
      })
    },
    onDeleteClick(healthcheckId) {
      this.confirmDelete(
        'Delete Healthcheck',
        'Are you sure you want to delete this healthcheck?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('cdn/removeHealthcheck', {
              distributionId: this.id,
              id: healthcheckId,
            })
            .then(() => {
              this.successToast('Healthcheck was removed')
              this.selected = []
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.tag {
  box-sizing: border-box;
  border-radius: 20px;
  padding: 2px 8px;
  color: var(--white);
  background: var(--primary);
  border: 1px solid var(--primary);
  margin-right: 5px;
}
</style>
